// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module AnalyticsPageErrorBoundary
 * @package @geenius/analytics/react-css
 * @description Page-level error boundary shared by route-ready analytics
 * compositions. It catches render-time failures and surfaces the standard
 * analytics recovery state.
 */

import type React from "react";
import { Component } from "react";
import { Card, CardContent } from "@geenius/ui/react-css";
import { ErrorState } from "./ErrorState";

interface AnalyticsPageErrorBoundaryProps {
  children: React.ReactNode;
  onRetry?: () => void;
}

interface AnalyticsPageErrorBoundaryState {
  error: Error | null;
}

/**
 * Captures render-time failures for route-ready analytics pages.
 */
export class AnalyticsPageErrorBoundary extends Component<
  AnalyticsPageErrorBoundaryProps,
  AnalyticsPageErrorBoundaryState
> {
  state: AnalyticsPageErrorBoundaryState = { error: null };

  static getDerivedStateFromError(
    error: Error,
  ): AnalyticsPageErrorBoundaryState {
    return { error };
  }

  componentDidCatch(): void {}

  private handleRetry = (): void => {
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <Card data-analytics-page-error>
          <CardContent>
            <ErrorState error={this.state.error} onRetry={this.handleRetry} />
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}
